import { Article, CargoFreight, HandlingUnit, isCargoFreight, Packable, Shipment, TransportOrder } from "./transportOrder"
import isEqual from "lodash/isEqual"

export const flattenHandlingUnits = (transportOrder: TransportOrder): TransportOrder =>
    transportOrder._embedded === undefined ? transportOrder : {
        ...transportOrder,
        _embedded: {
            ...transportOrder._embedded,
            shipments: transportOrder._embedded.shipments.map(flattenHandlingUnitsOfShipment),
        }
    }

export const flattenHandlingUnitsOfShipment = (shipment: Shipment): Shipment =>
    isCargoFreight(shipment.freight) ? {
        ...shipment,
        freight: flattenHandlingUnitsOfCargoFreight(shipment.freight),
    } : shipment

const flattenHandlingUnitsOfCargoFreight = (freight: CargoFreight): CargoFreight => ({
    ...freight,
    handling_units: freight.handling_units.map(pullUpArticles)
})

export const pullUpArticles = (handlingUnit: HandlingUnit): Omit<HandlingUnit, "content"> & { content: Article[] } => {
    const articles = pullUpArticlesFromPackables(handlingUnit.content)
    return {
        ...handlingUnit,
        content: groupArticles(articles),
    }
}

const groupArticles = (articles: Article[]): Article[] =>
    articles.reduce<Article[]>((groupedArticles, article) => {
        const groupedArticleIndex = groupedArticles.findIndex(groupedArticle => hasSameGroupingKey(groupedArticle, article))
        if (groupedArticleIndex === -1) {
            groupedArticles.push(article)
        } else {
            const groupedArticle = groupedArticles[groupedArticleIndex]
            groupedArticles[groupedArticleIndex] = {
                ...groupedArticle,
                quantity: addQuantities(groupedArticle.quantity, article.quantity),
            }
        }
        return groupedArticles
    }, [])

const hasSameGroupingKey = (first: Article, second: Article): boolean =>
    first.article_id === second.article_id &&
    isEqual(first.dangerous_goods_information, second.dangerous_goods_information) &&
    isEqual(first.country_of_origin, second.country_of_origin) &&
    isEqual(first.net_explosive_mass, second.net_explosive_mass) &&
    first.internal_place_of_destination === second.internal_place_of_destination

const addQuantities = (first: number | undefined, second: number | undefined): number | undefined =>
    first === undefined || second === undefined ? undefined : first + second

const pullUpArticlesFromPackables = (packables: Packable[]): Article[] => packables.flatMap(pullUpArticlesFromPackable)

const pullUpArticlesFromPackable = (packable: Packable): Article[] => {
    if (packable.type === "ARTICLE") {
        return [packable]
    } else {
        return pullUpArticlesFromPackables(packable.content.map(it => multiplyQuantity(it, packable.quantity)))
    }
}

const multiplyQuantity = (packable: Packable, qty: number | undefined): Packable => {
    const multiply = (a: number | null | undefined, b: number | null | undefined): number | undefined =>
        a === undefined || a === null || b === undefined || b === null ? undefined : a * b

    return { ...packable, quantity: multiply(packable.quantity, qty) }
}
